import Decimal from 'decimal.js';
import numbro from 'numbro';
import formatDate from './dateFormatterFunction';

// Helper function to format number

// Given a number this function returns an abbreviated number
// Eg 1000000 will be returned as 1M
// Uses numbro library only for numeric formatting; the abbreviation
// step mimics the original logic where the first digit before the
// decimal is always >= 1 (never something like 0.5M).
export const formatNumberWithAbbrev = (value: any, digits: any) => {
  const numValue = Number(value);

  // Handle non-numeric values
  if (numValue === null || isNaN(numValue)) {
    return value;
  }

  // For small numbers (< 1000), just apply rounding (no abbreviation)
  if (Math.abs(numValue) < 1000) {
    return numValue.toFixed(digits);
  }

  // Decide the abbreviation so that the first digit before the decimal
  // is never 0, as we doing before in old implementation
  const absVal = Math.abs(numValue);

  type AbbrevConfig = { threshold: number; suffix: string };
  const abbrevs: AbbrevConfig[] = [
    { threshold: 1e12, suffix: 'T' }, // Trillion
    { threshold: 1e9, suffix: 'B' }, // Billion
    { threshold: 1e6, suffix: 'M' }, // Million
    { threshold: 1e3, suffix: 'K' }, // Thousand
  ];

  const chosen = abbrevs.find((a) => absVal >= a.threshold);

  // Fallback to plain rounding if somehow no threshold matches
  if (!chosen) {
    return numValue.toFixed(digits);
  }

  const scaled = numValue / chosen.threshold;

  const formattedNumber = numbro(scaled).format({
    mantissa: digits,
    trimMantissa: false,
  });

  return `${formattedNumber.toUpperCase()}${chosen.suffix}`;
};

// Given a number this function returns comma separated number
// Eg 1000000 will be returned as 1,000,000
export const formatNumberWithComma = (value: any) => {
  const commas = value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return commas;
};

// Function that formats numbers in labels for a chart
// Rounding off digits, adding commas or abbreviations, add a currency Symbol, etc
export const formatChartLabelValue = (chartControl: any, value: any) => {
  // If Rounding enabled, returns a rounded value
  if (chartControl.formatOptions.labelFormats.enableRounding) {
    value = Number(value).toFixed(chartControl.formatOptions.labelFormats.roundingDigits);
  }

  // Returns an appreviated value along with required rounding off
  if (chartControl.formatOptions.labelFormats.numberSeparator === 'Abbrev') {
    const text = value.toString();
    let index = text.indexOf('.');
    if (index === -1) {
    }
    var roundOriginalDigits = text.length - index - 1;

    value = formatNumberWithAbbrev(
      value,
      chartControl.formatOptions.labelFormats.enableRounding
        ? chartControl.formatOptions.labelFormats.roundingDigits
        : roundOriginalDigits,
    );
  }

  // Returns a comma separated value of number
  if (chartControl.formatOptions.labelFormats.numberSeparator === 'Comma') {
    value = formatNumberWithComma(value);
  }

  // Returns value with currency symbol of user's choice
  if (chartControl.formatOptions.labelFormats.formatValue === 'Currency')
    value = `${chartControl.formatOptions.labelFormats.currencySymbol} ${value}`;

  // Retuns value with a % suffix
  if (chartControl.formatOptions.labelFormats.formatValue === 'Percent') value = `${value} %`;

  return value;
};

export const formatChartLabelValueForSelectedMeasure = (
  chartControl: any,
  chartProperties: any,
  value: any,
  columnName: string, // this is the displayname of the measure
  dateFormats?: { [key: string]: string },
) => {
  if (dateFormats) {
    value = formatDate(value?.toString(), dateFormats);
  } else {
    let measureAxis = chartProperties.chartAxes.find((item: any) => item.name === 'Measure');
    if (chartProperties.chartType === 'scatterPlot') {
      measureAxis = chartProperties.chartAxes[2].fields;
      measureAxis = measureAxis.concat(chartProperties.chartAxes[3].fields);
    }
    if (!measureAxis) {
      console.warn('Measure axis not found');
      return value;
    }

    let field;
    if (chartProperties.chartType === 'scatterPlot') {
      const dimensionFields = chartProperties.chartAxes[1]?.fields || [];
      field = dimensionFields.find((val: any) => val?.displayname === columnName);

      if (!field) {
        let measures = chartProperties.chartAxes[2]?.fields || [];
        measures = measures.concat(chartProperties.chartAxes[3]?.fields || []);
        field = measures.find((val: any) => val?.displayname === columnName);
      }
    } else if (measureAxis) {
      field = measureAxis.fields.find((val: any) => val?.displayname === columnName);
    }

    if (!field) {
      console.warn(`Field with displayname "${columnName}" not found`);
      return value;
    }

    const uId = field.uId;
    const measureFormat = chartControl.formatOptions.labelFormats?.measureFormats[uId];

    if (!measureFormat) {
      return value;
    }

    if (!(columnName.length > 0)) {
      return value;
    }

    // value = removeTrailingZeros(value);

    if (measureFormat?.percentageCalculate) {
      value = new Decimal(value).times(100);
    }

    if (measureFormat?.enableRounding) {
      value = Number(value).toFixed(measureFormat.roundingDigits);
    }

    if (measureFormat?.numberSeparator === 'Abbrev') {
      const valueTemp = value;

      const text = valueTemp?.toString();
      let index = text.indexOf('.');
      if ((index = -1)) {
      }
      var roundOriginalDigits = text?.length - index - 1;

      value = formatNumberWithAbbrev(
        value,
        measureFormat.enableRounding ? measureFormat.roundingDigits : roundOriginalDigits,
      );
    }

    if (measureFormat?.numberSeparator === 'Comma') {
      value = formatNumberWithComma(value);
    }

    if (measureFormat?.formatValue === 'Currency') {
      value = `${measureFormat.currencySymbol} ${value}`;
    }

    if (measureFormat?.formatValue === 'Percent') {
      value = `${value} %`;
    }
  }
  return value;
};

export const formatTooltipValueForSelectedMeasure = (
  currentUid: string,
  tooltipFormats: any,
  value: any,
) => {
  const tooltipAllUid = Object.keys(tooltipFormats.measureFormats);
  let measureFormat = tooltipFormats.measureFormats[currentUid];

  if (tooltipAllUid.includes(currentUid)) {
    if (measureFormat.finalDateFormat) {
      value = formatDate(value?.toString(), measureFormat);
    } else {
      if (measureFormat.percentageCalculate) {
        value = new Decimal(value).times(100);
      }
      if (measureFormat.enableRounding) {
        value = Number(value).toFixed(measureFormat.roundingDigits);
      }
      if (measureFormat.numberSeparator === 'Abbrev') {
        const valueTemp = value;

        const text = valueTemp.toString();
        let index = text.indexOf('.');
        if ((index = -1)) {
        }
        var roundOriginalDigits = text.length - index - 1;

        value = formatNumberWithAbbrev(
          value,
          measureFormat.enableRounding ? measureFormat.roundingDigits : roundOriginalDigits,
        );
      }

      if (measureFormat.numberSeparator === 'Comma') {
        value = formatNumberWithComma(value);
      }

      if (measureFormat.formatValue === 'Currency') {
        value = `${measureFormat.currencySymbol} ${value}`;
      }

      if (measureFormat.formatValue === 'Percent') {
        value = `${value} %`;
      }
    }
  }
  return value;
};

export const formatChartYAxisValueForSelectedMeasure = (
  chartControl: any,
  chartProperties: any,
  value: any,
  columnName: string, // this is the displayname of the measure
) => {};

// Similar to above function. But formatting done for Y axis values in a chart
export const formatChartYAxisValue = (
  chartControl: any,
  value: any,
  dateFormats?: { [key: string]: string },
  seriesNumber: string = '1',
) => {
  if (dateFormats) {
    value = formatDate(value.toString(), dateFormats);
  } else {
    if (chartControl.formatOptions.yAxisFormats[seriesNumber].percentageCalculate) {
      value = new Decimal(value).times(100);
    }
    // If Rounding enabled, returns a rounded value
    if (chartControl.formatOptions.yAxisFormats[seriesNumber].enableRounding) {
      value = Number(value).toFixed(
        chartControl.formatOptions.yAxisFormats[seriesNumber].roundingDigits,
      );
    }

    // Returns an appreviated value along with required rounding off
    if (
      chartControl.formatOptions.yAxisFormats[seriesNumber].formatValue !== 'Percent' &&
      chartControl.formatOptions.yAxisFormats[seriesNumber].numberSeparator === 'Abbrev'
    ) {
      const text = value.toString();
      let index = text.indexOf('.');
      if ((index = -1)) {
      }
      var roundOriginalDigits = text.length - index - 1;

      value = formatNumberWithAbbrev(
        value,
        chartControl.formatOptions.yAxisFormats[seriesNumber].enableRounding
          ? chartControl.formatOptions.yAxisFormats[seriesNumber].roundingDigits
          : roundOriginalDigits,
      );
    }

    // Returns a comma separated value of number
    if (chartControl.formatOptions.yAxisFormats[seriesNumber].numberSeparator === 'Comma') {
      value = formatNumberWithComma(value);
    }

    // Returns value with currency symbol of user's choice
    if (chartControl.formatOptions.yAxisFormats[seriesNumber].formatValue === 'Currency') {
      value = `${chartControl.formatOptions.yAxisFormats[seriesNumber].currencySymbol} ${value}`;
    }

    // Retuns value with a % suffix

    if (chartControl.formatOptions.yAxisFormats[seriesNumber].formatValue === 'Percent') {
      value = `${value} %`;
    }
  }
  return value;
};

// Similar to above function. But formatting done for X axis values in a chart
export const formatChartXAxisValue = (
  chartControl: any,
  value: any,
  dateFormats?: { [key: string]: string },
  seriesNumber: string = '1',
) => {
  if (dateFormats) {
    value = formatDate(value.toString(), dateFormats);
  } else {
    if (chartControl.formatOptions.xAxisFormats[seriesNumber].percentageCalculate) {
      value = new Decimal(value).times(100);
    }

    // If Rounding enabled, returns a rounded value
    if (chartControl.formatOptions.xAxisFormats[seriesNumber].enableRounding) {
      value = Number(value).toFixed(
        chartControl.formatOptions.xAxisFormats[seriesNumber].roundingDigits,
      );
    }

    // Returns an appreviated value along with required rounding off
    if (
      chartControl.formatOptions.xAxisFormats[seriesNumber].formatValue !== 'Percent' &&
      chartControl.formatOptions.xAxisFormats[seriesNumber].numberSeparator === 'Abbrev'
    ) {
      const text = value.toString();
      let index = text.indexOf('.');
      if ((index = -1)) {
      }
      const roundOriginalDigits = text.length - index - 1;

      value = formatNumberWithAbbrev(
        value,
        chartControl.formatOptions.xAxisFormats[seriesNumber].enableRounding
          ? chartControl.formatOptions.xAxisFormats[seriesNumber].roundingDigits
          : roundOriginalDigits,
      );
    }

    // Returns a comma separated value of number
    if (chartControl.formatOptions.xAxisFormats[seriesNumber].numberSeparator === 'Comma') {
      value = formatNumberWithComma(value);
    }
    // Returns value with currency symbol of user's choice
    if (chartControl.formatOptions.xAxisFormats[seriesNumber].formatValue === 'Currency') {
      value = `${chartControl.formatOptions.xAxisFormats[seriesNumber].currencySymbol} ${value}`;
    }

    // Retuns value with a % suffix

    if (chartControl.formatOptions.xAxisFormats[seriesNumber].formatValue === 'Percent') {
      value = `${value} %`;
    }
  }

  return value;
};

function removeTrailingZeros(number: number) {
  return number.toString().replace(/\.0+$/, '');
}

export function gaugeChartFormatter(gaugeChartFormats: any, formatName: string, value: any) {
  if (gaugeChartFormats[formatName]?.percentageCalculate) {
    value = new Decimal(value).times(100);
  }
  if (gaugeChartFormats[formatName]?.enableRounding) {
    value = Number(value).toFixed(gaugeChartFormats[formatName].roundingDigits);
  }
  if (gaugeChartFormats[formatName]?.numberSeparator === 'Abbrev') {
    const valueTemp = value;

    const text = valueTemp.toString();
    let index = text.indexOf('.');
    if ((index = -1)) {
    }
    var roundOriginalDigits = text.length - index - 1;

    value = formatNumberWithAbbrev(
      value,
      gaugeChartFormats[formatName].enableRounding
        ? gaugeChartFormats[formatName].roundingDigits
        : roundOriginalDigits,
    );
  }

  if (gaugeChartFormats[formatName]?.numberSeparator === 'Comma') {
    value = formatNumberWithComma(value);
  }

  if (gaugeChartFormats[formatName]?.formatValue === 'Currency') {
    value = `${gaugeChartFormats[formatName].currencySymbol} ${value}`;
  }

  if (gaugeChartFormats[formatName]?.formatValue === 'Percent') {
    value = `${value} %`;
  }
  return value;
}
